import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../utlis/axiosInstance";
import { requestNotificationPermission } from "../config/firebase";
import { toast } from "react-toastify";

const Login: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const validateEmail = (email: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!re.test(email.toLowerCase())) {
      setEmailError("Invalid email address");
      return false;
    } else {
      setEmailError("");
      return true;
    }
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const saveFcmToken = async () => {
    try {
      const token = await requestNotificationPermission();
      if (!token) {
        toast.warning("Could not get FCM token. Please allow notifications.");
        return;
      }
      await axiosInstance.post("/gpstracker/firebasehandler/", {
        token,
        device_type: "web",
      });
    } catch (err) {
      console.error("Login error:", err);
      setError("Please check your email or password.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        "/gpstracker/usersessionhandler/",
        { email, password }
      );
      const responseData = response.data;

      // Handle different response formats
      let parsedData;
      if (typeof responseData === "string") {
        // If response is a string, try to parse it
        try {
          const cleanedData = responseData.replace(/Infinity/g, "null");
          parsedData = JSON.parse(cleanedData);
        } catch (parseError) {
          console.error("Error parsing string response:", parseError);
          setError("Unexpected response format from server.");
          return;
        }
      } else if (typeof responseData === "object" && responseData !== null) {
        // If response is already an object, use it directly
        parsedData = responseData;
      } else {
        setError("Unexpected response format from server.");
        return;
      }

      if (parsedData.responseData) {
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("user", JSON.stringify(parsedData.responseData));
        
        // Save email and password to localStorage for easy switching back
        localStorage.setItem("savedEmail", email.trim());
        localStorage.setItem("savedPassword", password);
        
        await saveFcmToken();

        // Dispatch custom login event to trigger theme reload
        window.dispatchEvent(new CustomEvent('userLogin'));

        window.location.href = "/";
      } else {
        setError("Invalid response from server.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: string) => {
    setLoading(true);
    setTimeout(() => {
      localStorage.setItem("isAuthenticated", "true");

      // Dispatch custom login event to trigger theme reload
      window.dispatchEvent(new CustomEvent('userLogin'));

      setLoading(false);
      navigate("/");
    }, 1500);
  };

  return (
    // <Box
    //   sx={{
    //     height: '100vh',
    //     display: 'flex',
    //     overflow: 'hidden'
    //   }}
    // >
    //   {/* Left side - Image */}
    //   {!isMobile && (
    //     <Box
    //       sx={{
    //         flex: '1 1 50%',
    //         position: 'relative',
    //         display: { xs: 'none', md: 'block' }
    //       }}
    //     >
    //       <Box
    //         sx={{
    //           position: 'absolute',
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           backgroundImage: 'url(https://images.unsplash.com/photo-1626450442362-b72bcd9b8b74?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80)',
    //           backgroundSize: 'cover',
    //           backgroundPosition: 'center'
    //         }}
    //       />

    //     </Box>
    //   )}

    //   {/* Right side - Form */}
    //   <Box
    //     sx={{
    //       flex: { xs: '1 1 100%', md: '1 1 50%' },
    //       display: 'flex',
    //       flexDirection: 'column',
    //       justifyContent: 'center',
    //       overflowY: 'auto',
    //       bgcolor: 'background.default',
    //       p: { xs: 2, sm: 4 }
    //     }}
    //   >
    //     <Container maxWidth="sm">
    //       <Paper
    //         elevation={isMobile ? 3 : 0}
    //         sx={{
    //           p: { xs: 3, sm: 4 },
    //           borderRadius: 2,
    //           bgcolor: 'background.paper'
    //         }}
    //       >

    //         <Typography variant="h4" component="h1" align="center" gutterBottom>
    //           Welcome Back
    //         </Typography>

    //         <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 4 }}>
    //           Sign in to continue to Fleet Management System
    //         </Typography>

    //         {error && (
    //           <Alert severity="error" sx={{ mb: 3 }}>
    //             {error}
    //           </Alert>
    //         )}

    //         <Box component="form" onSubmit={handleSubmit} noValidate>
    //           <TextField
    //             margin="normal"
    //             required
    //             fullWidth
    //             id="email"
    //             label="Email Address"
    //             name="email"
    //             autoComplete="email"
    //             autoFocus
    //             value={email}
    //             onChange={(e) => setEmail(e.target.value)}
    //             onBlur={() => validateEmail(email)}
    //             error={!!emailError}
    //             helperText={emailError}
    //             InputProps={{
    //               startAdornment: (
    //                 <InputAdornment position="start">
    //                   <EmailIcon color="action" />
    //                 </InputAdornment>
    //               ),
    //             }}
    //           />

    //           <TextField
    //             margin="normal"
    //             required
    //             fullWidth
    //             name="password"
    //             label="Password"
    //             type={showPassword ? 'text' : 'password'}
    //             id="password"
    //             autoComplete="current-password"
    //             value={password}
    //             onChange={(e) => setPassword(e.target.value)}
    //             onBlur={() => validatePassword(password)}
    //             error={!!passwordError}
    //             helperText={passwordError}
    //             InputProps={{
    //               startAdornment: (
    //                 <InputAdornment position="start">
    //                   <LockIcon color="action" />
    //                 </InputAdornment>
    //               ),
    //               endAdornment: (
    //                 <InputAdornment position="end">
    //                   <IconButton
    //                     aria-label="toggle password visibility"
    //                     onClick={() => setShowPassword(!showPassword)}
    //                     edge="end"
    //                   >
    //                     {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
    //                   </IconButton>
    //                 </InputAdornment>
    //               ),
    //             }}
    //           />

    //           <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    //             <Link component={RouterLink} to="/forgot-password" variant="body2">
    //               Forgot password?
    //             </Link>
    //           </Box>

    //           <Button
    //             type="submit"
    //             fullWidth
    //             variant="contained"
    //             disabled={loading}
    //             sx={{ mt: 3, mb: 2, py: 1.5 }}
    //           >
    //             {loading ? <CircularProgress size={24} /> : 'Sign In'}
    //           </Button>

    //           <Divider sx={{ my: 3 }}>
    //             <Typography variant="body2" color="text.secondary">
    //               OR
    //             </Typography>
    //           </Divider>
    //           <Box sx={{ textAlign: 'center' }}>
    //             <Typography variant="body2">
    //               Don't have an account?{' '}
    //               <Link component={RouterLink} to="/signup" variant="body2">
    //                 Sign up
    //               </Link>
    //             </Typography>
    //           </Box>
    //         </Box>
    //       </Paper>
    //     </Container>
    //   </Box>
    // </Box>
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Left side - Video Background with Content */}
      {!isMobile && (
        <Box
          sx={{
            flex: "1 1 70%",
            position: "relative",
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {/* Video Background */}
          <Box
            component="video"
            autoPlay
            loop
            muted
            playsInline
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
          >
            <source src="/login.mp4" type="video/mp4" />
            {/* Fallback image if video doesn't load */}
            <Box
              component="img"
              src=""
              alt="Telematics background"
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* Content Overlay */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.0)",
              color: "white",
              p: 8,
              borderRadius: 2,
              maxWidth: "90%",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 4 }}
            >
              Shaping the future of Telematics
            </Typography>

            <Typography variant="h6" component="h2" sx={{ mb: 6 }}>
              Driving the future of EV-Telematics: Trucking, Employee Transport,
              School Bus and Video Telematics
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 4,
                mb: 6,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  15 +
                </Typography>
                <Typography variant="subtitle1">Years of Business</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  200 +
                </Typography>
                <Typography variant="subtitle1">Solutions</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  5K +
                </Typography>
                <Typography variant="subtitle1">Happy Customers</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  55K +
                </Typography>
                <Typography variant="subtitle1">Happy Trips</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Right side - Login Form */}
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 30%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflowY: "auto",
          bgcolor: "background.default",
          p: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                style={{
                  width: "50px",
                  height: "50px",
                  marginBottom: "16px",
                }}
              />
            </div>

            <Typography
              variant="subtitle1"
              color="textSecondary"
              className="logo"
            >
              Login to account
            </Typography>
          </Box>

          <Paper
            elevation={isMobile ? 3 : 0}
            sx={{
              p: { xs: 3, sm: 3 },
              borderRadius: 2,
              bgcolor: "transparent",
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => validateEmail(email)}
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => validatePassword(password)}
                error={!!passwordError}
                helperText={passwordError}
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot password ?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontWeight: "bold",
                  bgcolor: "var(--primary-color)",
                  "&:hover": {
                    bgcolor: "var(--primary-light)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Login"}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
